// Google Consent Mode v2 wiring.
//
// Default state: deny everything until the user accepts. This must run
// BEFORE any tag (gtag, fbq, etc.) loads, so we install it from
// src/main.tsx via a side-effect import.

type ConsentState = "granted" | "denied";

interface ConsentSettings {
  ad_storage: ConsentState;
  ad_user_data: ConsentState;
  ad_personalization: ConsentState;
  analytics_storage: ConsentState;
  functionality_storage: ConsentState;
  security_storage: ConsentState;
}

const STORAGE_KEY = "pk_consent_v2";

function loadStoredConsent(): ConsentSettings | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ConsentSettings) : null;
  } catch {
    return null;
  }
}

function saveConsent(c: ConsentSettings) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(c));
  } catch {
    // ignore
  }
}

function gtag(...args: unknown[]) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  // GTM consent commands are pushed as array-shaped entries on dataLayer.
  (window.dataLayer as unknown[]).push(args);
}

/**
 * Install default consent state. Call once, as early as possible.
 * Defaults to denied (EU-safe). If the user has already accepted in a
 * previous session, immediately upgrade to granted.
 */
export function installConsentDefaults() {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];

  // Default: deny everything except security.
  gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    functionality_storage: "denied",
    security_storage: "granted",
    wait_for_update: 500,
  });

  const stored = loadStoredConsent();
  if (stored) {
    gtag("consent", "update", stored);
    if (stored.ad_storage === "granted") {
      const fbq = (window as unknown as { fbq?: (...a: unknown[]) => void }).fbq;
      if (typeof fbq === "function") fbq("consent", "grant");
    }
  }
}

export function grantAllConsent() {
  const all: ConsentSettings = {
    ad_storage: "granted",
    ad_user_data: "granted",
    ad_personalization: "granted",
    analytics_storage: "granted",
    functionality_storage: "granted",
    security_storage: "granted",
  };
  saveConsent(all);
  gtag("consent", "update", all);
  // Meta Pixel consent
  const fbq = (window as unknown as { fbq?: (...a: unknown[]) => void }).fbq;
  if (typeof fbq === "function") fbq("consent", "grant");
}

export function denyOptionalConsent() {
  const denied: ConsentSettings = {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    functionality_storage: "denied",
    security_storage: "granted",
  };
  saveConsent(denied);
  gtag("consent", "update", denied);
  const fbq = (window as unknown as { fbq?: (...a: unknown[]) => void }).fbq;
  if (typeof fbq === "function") fbq("consent", "revoke");
}

export function hasMadeConsentChoice(): boolean {
  return loadStoredConsent() !== null;
}
